// src/components/books/BookAccessGuard.jsx - FIXED VERSION
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock, LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { bookAccessService } from '../../services/bookAccessService';

export const BookAccessGuard = ({ bookId, children, isPublicRoute = false }) => {
  const { user } = useAuth();
  const [accessResult, setAccessResult] = useState({ loading: true });

  useEffect(() => {
    checkAccess();
  }, [bookId, user]);

  const checkAccess = async () => {
    try {
      // FIRST: Check if book data was server-injected
      if (window.__BOOK_DATA__ && window.__BOOK_DATA__.id === bookId) {
          
        // Use server data and skip Firestore query
        const book = window.__BOOK_DATA__;
        
        // Perform basic access check without fetching from Firestore
        const accessCheck = performClientSideAccessCheck(book, user);
        
        setAccessResult({ 
          ...accessCheck, 
          book, 
          loading: false 
        });
        return;
      }

      const result = await bookAccessService.canAccessBook(bookId, user?.uid);
      setAccessResult({ ...result, loading: false });
      
    } catch (error) {
      console.error('Error checking access:', error);
      setAccessResult({ 
        canAccess: false, 
        reason: 'Error checking access', 
        loading: false 
      });
    }
  };

  // Client-side access check using server-injected book data
  const performClientSideAccessCheck = (book, user) => {
    // Book exists (since server found it)
    if (!book) {
      return { canAccess: false, reason: 'Book not found' };
    }

    // Check if book is published
    if (!book.published) {
      return { canAccess: false, reason: 'Book not available' };
    }

    // Public access
    if (book.accessLevel === 'public') {
      return { canAccess: true };
    }

    // Private book - needs authentication
    if (book.accessLevel === 'private') {
      if (!user) {
        return { canAccess: false, reason: 'Authentication required' };
      }

      // Owner access
      if (book.createdBy === user.uid) {
        return { canAccess: true };
      }

      // Check if user is in allowed list
      if (book.allowedUsers && book.allowedUsers.includes(user.uid)) {
        return { canAccess: true };
      }

      return { canAccess: false, reason: 'Access denied' };
    }

    // Default to requiring access check
    return { canAccess: true };
  };

  if (accessResult.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  if (!accessResult.canAccess) {
    return (
      <BookAccessDenied 
        reason={accessResult.reason} 
        bookId={bookId} 
        isPublicRoute={isPublicRoute}
      />
    );
  }

  // IMPORTANT: Pass book data to children to avoid duplicate fetching
  return React.cloneElement(children, { 
    bookData: accessResult.book,
    bookId 
  });
};

const BookAccessDenied = ({ reason, bookId, isPublicRoute }) => {
  const getContent = () => {
    switch (reason) {
      case 'Authentication required':
        return {
          icon: <LogIn className="h-16 w-16 text-blue-500 mx-auto mb-4" />,
          title: 'Sign In Required',
          message: 'This book requires you to sign in to access it.',
          action: isPublicRoute ? (
            <button
              onClick={() => window.location.href = `/login?redirect=/books/${bookId}`}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
            >
              Sign In to Continue
            </button>
          ) : (
            <button
              onClick={() => window.location.href = '/login'}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
            >
              Sign In
            </button>
          )
        };
      
      case 'Access denied':
        return {
          icon: <Lock className="h-16 w-16 text-red-500 mx-auto mb-4" />,
          title: 'Access Denied',
          message: 'You don\'t have permission to view this book.',
          action: isPublicRoute ? (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                This book is private. Contact the owner for access.
              </p>
            </div>
          ) : (
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
            >
              Go to Dashboard
            </button>
          )
        };

      case 'Book not found':
      case 'Book not available':
        return {
          icon: <AlertCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />,
          title: 'Book Not Available',
          message: 'The book you\'re looking for is not available.',
          action: isPublicRoute ? (
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
            >
              Go Home
            </button>
          ) : null
        };

      default:
        return {
          icon: <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />,
          title: 'Access Error',
          message: reason || 'An error occurred while checking access to this book.',
          action: isPublicRoute ? null : (
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
            >
              Try Again
            </button>
          )
        };
    }
  };

  const content = getContent();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md mx-auto p-6">
        {content.icon}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{content.title}</h1>
        <p className="text-gray-600 mb-6">{content.message}</p>
        {content.action}
      </div>
    </div>
  );
};

export default BookAccessGuard;